// バージョンチェックを実行するパスのリスト
// このリストに含まれるページでのみバージョンチェックを実行する
const ENABLE_VERSION_CHECK_PATHS = [
  // メインページや一覧画面など、編集中でないページの例
  "/",
  "/pricing",
  "/announcements",
  "/preferences/settings",
  "/preferences/account",
  "/preferences/plan-and-billings",
  "/preferences/enterprise",
  "/preferences/misc",
  "/mind-map",
  "/notes-nebula",
  "/juno",
  "/juno/videos",
  "/juno/gallery",
  "/juno/likes",
  "/juno/models",
  "/juno/image-generator",
  "/juno/video-generator",
  "/juno/sounds/music-generator",
];

// クッキーからバージョンを取得する関数
const getStoredVersion = (): string | null => {
  const match = document.cookie.match(/appVersion=([^;]+)/);
  return match ? match[1] : null;
};

// クッキーにバージョンを保存する関数
const storeVersion = (version: string): void => {
  // 30日間有効なクッキーを設定
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 30);
  document.cookie = `appVersion=${version};expires=${expiryDate.toUTCString()};path=/`;
};

// 現在のページでバージョンチェックを実行すべきかを判断する関数
const shouldCheckVersion = (): boolean => {
  const fullPath = window.location.pathname;

  // 一時的な無効フラグがセッションストレージに設定されているかチェック
  if (sessionStorage.getItem("disableVersionCheck") === "true") {
    return false;
  }

  // 言語パスのみの場合（/ja や /en）はルートパスと同等に扱う
  if (fullPath === "/ja" || fullPath === "/en") {
    return true; // 常に言語ルートパスではバージョンチェックを実行
  }

  // 言語プレフィックス（/ja/や/en/など）を除去してパスを正規化
  const pathWithoutLang = fullPath.replace(/^\/(ja|en)\//, "/");

  // パスが直接リストに含まれているか確認
  // 完全一致するパスがあるかチェック
  const exactMatch = ENABLE_VERSION_CHECK_PATHS.includes(pathWithoutLang);
  if (exactMatch) {
    return true;
  }

  // ルートパスもしくはメインパスのみのチェック
  // 最初のパス部分を抽出（例: /juno/videos から /juno を取得）
  const mainPath = pathWithoutLang.match(/^\/[^/]*/)?.[0] || "";

  // メインパスのみのチェック（サブパスのないページのみが対象）
  if (pathWithoutLang === mainPath) {
    const shouldCheck = ENABLE_VERSION_CHECK_PATHS.some((path) => {
      return mainPath === path || mainPath + "/" === path;
    });

    return shouldCheck;
  }

  return false;
};

// バックエンドからバージョン情報を取得する関数
export const fetchBackendVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch("/api/v1/version");
    if (!response.ok) {
      throw new Error("バージョン情報の取得に失敗しました");
    }
    const data = await response.json();
    return data.version;
  } catch {
    return null;
  }
};

// バージョンチェックを行い、不一致であれば自動的にリロードする関数
export const checkAndReloadIfNeeded = async (): Promise<void> => {
  // タイムスタンプを更新
  // 編集ページなどではバージョンチェックを実行しない
  if (!shouldCheckVersion()) {
    return;
  }

  const backendVersion = await fetchBackendVersion();

  // バックエンドのバージョンが取得できない場合は何もしない
  if (!backendVersion) {
    return;
  }

  const storedVersion = getStoredVersion();

  // 初回アクセス時はバージョンを保存して終了
  if (!storedVersion) {
    storeVersion(backendVersion);
    return;
  }

  // バージョンが異なる場合はクッキーを更新してリロード
  if (storedVersion !== backendVersion) {
    console.log(`バージョン不一致を検出: バックエンド=${backendVersion}, 保存済み=${storedVersion}`);
    storeVersion(backendVersion);
    window.location.reload();
  }
};

// グローバルなインターバルIDを管理
let versionCheckIntervalId: number | null = null;

// 定期的にバージョンチェックを行う関数
export const startVersionCheckInterval = (intervalMinutes = 5): (() => void) => {
  // すでにインターバルが設定されている場合はクリア
  if (versionCheckIntervalId !== null) {
    clearInterval(versionCheckIntervalId);
    versionCheckIntervalId = null;
  }

  // 初回チェック
  checkAndReloadIfNeeded();

  // 定期的にチェック
  versionCheckIntervalId = window.setInterval(
    () => {
      checkAndReloadIfNeeded();
    },
    intervalMinutes * 60 * 1000
  );

  // クリーンアップ関数を返す
  return () => {
    if (versionCheckIntervalId !== null) {
      clearInterval(versionCheckIntervalId);
      versionCheckIntervalId = null;
    }
  };
};

// ページアクセス時やナビゲーション時にチェックを実行する関数
export const checkOnNavigation = (): void => {
  // 直ちにバージョンチェックを実行
  checkAndReloadIfNeeded();
};

// バージョンチェックを停止する関数
export const stopVersionCheck = (): void => {
  if (versionCheckIntervalId !== null) {
    clearInterval(versionCheckIntervalId);
    versionCheckIntervalId = null;
  }
};

// 編集状態などでバージョンチェックを一時的に無効にする
export const disableVersionCheckTemporarily = (): void => {
  // 一時的に無効フラグをセット（セッションストレージに保存）
  sessionStorage.setItem("disableVersionCheck", "true");
};

// バージョンチェックを再度有効にする
export const enableVersionCheck = (): void => {
  // 無効フラグを削除
  sessionStorage.removeItem("disableVersionCheck");
};
